import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Icon from "@/components/ui/Icon";
import Badge from "@/components/ui/Badge";
import FeedbackModal from "@/components/layout/FeedbackModal";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRoles } from "@/hooks/useUserRoles";
import { getMyAccounts, switchAccount } from "@/service";
import { MeAccount, USER_ROLES } from "@/types/auth";
import { NAV_MODULES, canSee } from "@/routes/navigation";
import { PATHS } from "@/routes/paths";
import { initials } from "@/utils/format";
import styles from "./styles.module.scss";
import { showApiError } from "@/utils/errorHandling";

const ACCOUNT_COLORS = ["#004aad", "#0f7a4a", "#a34000", "#1d3f73", "#00838f"];

interface AccountMenuProps {
  onNavigate: () => void;
}

// Conteúdo do menu da conta (aberto pelo botão da conta no topo): perfil,
// troca de conta, links de configuração filtrados pelo perfil e rodapé.
const AccountMenu: React.FC<AccountMenuProps> = ({ onNavigate }) => {
  const { user, account, logout } = useAuth();
  const { roles } = useUserRoles();
  const [accounts, setAccounts] = useState<MeAccount[]>([]);
  const [switching, setSwitching] = useState<string | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    getMyAccounts()
      .then((response) => setAccounts((response?.data ?? []).filter((a) => a.isActive !== false)))
      .catch(() => undefined);
  }, []);

  const currentAccountId = user?.accountId ?? account?.accountId;
  const primaryRole = roles[0];
  const links = (NAV_MODULES.find((m) => m.key === "conta")?.pages ?? []).filter(
    (page) => page.path !== PATHS.profile && canSee(page.roles, roles),
  );
  const limited = !roles.includes("account-admin") && !roles.includes("finance-manager");

  const handleSwitch = async (accountId: string) => {
    if (accountId === currentAccountId || switching) return;
    setSwitching(accountId);
    try {
      await switchAccount(accountId);
      // Recarrega o app inteiro para não sobrar dado da conta anterior.
      window.location.href = PATHS.dash;
    } catch (error) {
      showApiError(error, "Não foi possível trocar de conta.");
      setSwitching(null);
    }
  };

  return (
    <div className={styles.menu} role="menu" aria-label="Conta e usuário">
      <div className={styles.userRow}>
        <span className={styles.userAvatar}>{initials(user?.name)}</span>
        <div className={styles.userText}>
          <span className={styles.userName}>{user?.name}</span>
          <span className={styles.userEmail}>{user?.email}</span>
        </div>
        {primaryRole && <Badge tone="primarySoft">{USER_ROLES[primaryRole]}</Badge>}
        <Link to={PATHS.profile} className={styles.profileButton} role="menuitem" onClick={onNavigate}>
          Meu perfil
        </Link>
      </div>

      <div className={styles.columns}>
        <div className={styles.column}>
          <span className={styles.overline}>Conta</span>
          <div className={`${styles.accountList} ${accounts.length > 3 ? styles.accountListScroll : ""}`}>
            {accounts.length === 0 && account && (
              <div className={`${styles.account} ${styles.accountActive}`}>
                <span className={styles.accountBadge} style={{ background: ACCOUNT_COLORS[0] }}>
                  {initials(account.name)}
                </span>
                <span className={styles.accountText}>
                  <span className={styles.accountName}>{account.name}</span>
                  <span className={styles.accountSub}>{account.domain}</span>
                </span>
              </div>
            )}
            {accounts.map((item, index) => {
              const active = item.accountId === currentAccountId;
              const name = item.accountName || item.username || item.accountId;
              return (
                <button
                  key={item.accountId}
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  disabled={switching !== null}
                  className={`${styles.account} ${active ? styles.accountActive : ""}`}
                  onClick={() => handleSwitch(item.accountId)}
                >
                  <span
                    className={styles.accountBadge}
                    style={{ background: ACCOUNT_COLORS[index % ACCOUNT_COLORS.length] }}
                  >
                    {initials(name)}
                  </span>
                  <span className={styles.accountText}>
                    <span className={styles.accountName}>{name}</span>
                    {item.domain && <span className={styles.accountSub}>{item.domain}</span>}
                  </span>
                  {switching === item.accountId ? (
                    <Icon name="refresh" size={16} className={styles.spin} />
                  ) : (
                    active && <Icon name="check" size={18} strokeWidth={2.4} className={styles.check} />
                  )}
                </button>
              );
            })}
          </div>
          <Link to={PATHS.hireAccount} className={styles.addAccount} role="menuitem" onClick={onNavigate}>
            <Icon name="plus" size={16} strokeWidth={2.2} />
            Contratar nova conta
          </Link>
        </div>

        <div className={styles.column}>
          <span className={styles.overline}>Configurações da conta</span>
          {links.map((link) => (
            <Link key={link.path} to={link.path} className={styles.link} role="menuitem" onClick={onNavigate}>
              <span className={styles.linkIcon}>
                <Icon name={link.icon ?? "doc"} size={16} strokeWidth={1.9} />
              </span>
              {link.label}
            </Link>
          ))}
          {limited && (
            <span className={styles.note}>
              Planos, dados cadastrais e usuários são geridos pelo Administrador da conta.
            </span>
          )}
        </div>
      </div>

      <div className={styles.footer}>
        <a href="https://www.yuha.com.br/termos-de-uso/" target="_blank" rel="noopener noreferrer" role="menuitem">
          <span className={styles.footerIcon}>
            <Icon name="doc" size={16} strokeWidth={1.9} />
          </span>
          Termos de uso
        </a>
        <a href="https://www.yuha.com.br/privacidade/" target="_blank" rel="noopener noreferrer" role="menuitem">
          <span className={styles.footerIcon}>
            <Icon name="lock" size={16} strokeWidth={1.9} />
          </span>
          Privacidade
        </a>
        <button type="button" role="menuitem" onClick={() => setFeedbackOpen(true)}>
          <span className={styles.footerIcon}>
            <Icon name="chat" size={16} strokeWidth={1.9} />
          </span>
          Feedback
        </button>
        <button type="button" role="menuitem" className={styles.logout} onClick={() => void logout()}>
          <Icon name="logout" size={15} />
          Sair
        </button>
      </div>

      <FeedbackModal
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        defaultName={user?.name}
        defaultEmail={user?.email}
      />
    </div>
  );
};

export default AccountMenu;
